import React, { useEffect, useMemo, useState } from 'react';
import ProductImage from '../ProductImage';
import { saveAllStocks } from '../../services/stockService';
import Icon from '../Icon';

export default function StockTab({
  stockProducts,
  stockValues,
  setStockValues,
  onDataChanged,
  seller,
  onDirtyChange,
  loading,
}) {
  const [saved, setSaved] = useState({});
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState(null);
  useEffect(() => setSaved({}), [stockProducts]);
  const rows = useMemo(
    () =>
      stockProducts.flatMap((product) =>
        (product.variants || []).map((variant) => ({ product, variant }))
      ),
    [stockProducts]
  );
  const baseline = (variant) => saved[variant.id] ?? variant.stock;
  const value = (variant) => stockValues[variant.id] ?? baseline(variant);
  const changed = rows.filter(
    ({ variant }) =>
      value(variant) === '' || Number(value(variant)) !== baseline(variant)
  );
  const invalid = changed.some(
    ({ variant }) =>
      value(variant) === '' ||
      !Number.isInteger(Number(value(variant))) ||
      Number(value(variant)) < 0
  );
  useEffect(
    () => onDirtyChange?.(changed.length > 0),
    [changed.length, onDirtyChange]
  );
  const visible = rows.filter(
    ({ product, variant }) =>
      `${product.title} ${product.publicArticle || product.platformSku} ${product.sellerSku} ${variant.barcode || ''}`
        .toLowerCase()
        .includes(search.toLowerCase()) &&
      (filter === 'all' ||
        (filter === 'zero'
          ? Number(value(variant)) === 0
          : Number(value(variant)) <= 5))
  );
  const handleSave = async () => {
    if (!changed.length || invalid || saving) return;
    const updates = changed.map(({ variant }) => ({
      variantId: variant.id,
      stock: Number(value(variant)),
    }));
    setSaving(true);
    setNotice(null);
    try {
      await saveAllStocks(updates, seller.id);
      setSaved((current) => ({
        ...current,
        ...Object.fromEntries(
          updates.map((update) => [update.variantId, update.stock])
        ),
      }));
      setNotice({ type: 'success', text: 'Остатки сохранены' });
      onDataChanged?.();
    } catch (error) {
      setNotice({
        type: 'error',
        text:
          error.response?.data?.error ||
          'Не удалось сохранить остатки. Изменения остались в таблице — повторите попытку.',
      });
    } finally {
      setSaving(false);
    }
  };
  const reset = () => {
    setStockValues(
      Object.fromEntries(
        rows.map(({ variant }) => [variant.id, baseline(variant)])
      )
    );
    setNotice(null);
  };
  return (
    <section className="seller-inventory">
      <div className="seller-page-heading">
        <div>
          <span className="eyebrow">УПРАВЛЕНИЕ СКЛАДОМ</span>
          <h1>Остатки</h1>
          <p>Все размеры в одной таблице. Измените количество и сохраните.</p>
        </div>
        <span className="seller-count">{rows.length} SKU</span>
      </div>
      <div className="seller-list-tools">
        <label className="seller-search">
          <Icon name="search" size={18} />
          <input
            aria-label="Поиск остатков"
            placeholder="Название, артикул или штрихкод"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </label>
        <select
          aria-label="Фильтр остатков"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
        >
          <option value="all">Все остатки</option>
          <option value="low">Заканчиваются · до 5 шт.</option>
          <option value="zero">Нет в наличии</option>
        </select>
        <span>{visible.length} вариантов</span>
      </div>
      {notice && (
        <p
          className={`seller-notice seller-notice--${notice.type}`}
          role={notice.type === 'error' ? 'alert' : 'status'}
        >
          {notice.text}
        </p>
      )}
      <div className="seller-panel seller-table-wrap">
        <table className="seller-data-table">
          <thead>
            <tr>
              <th>Товар / артикул</th>
              <th>Размер</th>
              <th>Штрихкод</th>
              <th>Остаток, шт.</th>
              <th>Наличие</th>
            </tr>
          </thead>
          <tbody>
            {visible.map(({ product, variant }) => {
              const current = value(variant);
              const dirty =
                current === '' || Number(current) !== baseline(variant);
              const bad =
                current === '' ||
                !Number.isInteger(Number(current)) ||
                Number(current) < 0;
              return (
                <tr key={variant.id} className={dirty ? 'is-edited' : ''}>
                  <td>
                    <div className="seller-cell-product">
                      <ProductImage product={product} width={42} height={56} zoom />
                      <span>
                        <strong>{product.title}</strong>
                        <small>
                          {product.sellerSku || product.publicArticle || product.platformSku}
                        </small>
                      </span>
                    </div>
                  </td>
                  <td>{variant.size}</td>
                  <td className="seller-muted">{variant.barcode || '—'}</td>
                  <td>
                    <input
                      type="number"
                      min="0"
                      step="1"
                      disabled={saving}
                      aria-label={`Остаток ${product.title}, ${variant.size}`}
                      aria-invalid={bad}
                      value={current}
                      onChange={(e) => {
                        setStockValues((prev) => ({
                          ...prev,
                          [variant.id]: e.target.value,
                        }));
                        setNotice(null);
                      }}
                    />
                    {bad && (
                      <small className="seller-field-error">
                        Целое число от 0
                      </small>
                    )}
                  </td>
                  <td>
                    <span
                      className={`seller-status ${Number(current) > 5 ? 'is-active' : Number(current) > 0 ? 'is-low' : ''}`}
                    >
                      {Number(current) > 5
                        ? 'В наличии'
                        : Number(current) > 0
                          ? 'Заканчивается'
                          : 'Нет в наличии'}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {!visible.length && (
          <div className="market-empty">
            <Icon name="layers" size={28} />
            <h3>
              {loading
                ? 'Загружаем остатки…'
                : rows.length
                  ? 'Нет подходящих вариантов'
                  : 'Пока нет вариантов'}
            </h3>
            <p>
              {loading
                ? 'Это займёт несколько секунд.'
                : 'Измените фильтр или добавьте варианты в карточку товара.'}
            </p>
          </div>
        )}
      </div>
      <div className="seller-save-bar">
        <span>
          <Icon name={changed.length ? 'edit' : 'check'} size={18} />
          <span>
            {changed.length
              ? `Изменено вариантов: ${changed.length}`
              : 'Нет несохранённых изменений'}
            {invalid && (
              <small>Исправьте количество в выделенных строках</small>
            )}
          </span>
        </span>
        <div>
          {changed.length > 0 && (
            <button
              className="btn btn-outline"
              disabled={saving}
              onClick={reset}
            >
              Сбросить
            </button>
          )}
          <button
            className="btn btn-primary"
            disabled={!changed.length || invalid || saving}
            onClick={handleSave}
          >
            {saving ? 'Сохраняем…' : 'Сохранить остатки'}
          </button>
        </div>
      </div>
    </section>
  );
}
